import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FileText, RefreshCw, Loader2, Download, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../../config";

// Format an ISO timestamp into a compact European date+time.
const formatTs = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Pull a short, stable label out of the module path (e.g.
// "services.email_service.EMAIL_TEMPLATES" → "EMAIL_TEMPLATES").
const labelFromModule = (mod) => {
  if (!mod) return "?";
  const parts = mod.split(".");
  return parts[parts.length - 1];
};

/**
 * Admin dashboard widget — last N GDPR Translations Audit PDF downloads.
 *
 * Each row pins the snapshot of catalogue fingerprints at the time
 * of export so the inspector can cross-check that an old signed PDF
 * still matches the live `/admin/i18n-status` values.
 */
export const GdprAuditDownloadsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 5;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/email-preview/gdpr-audit-downloads?limit=${limit}`,
      );
      setData(r.data);
    } catch (e) {
      console.error("GDPR audit downloads load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="gdpr-audit-downloads-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-lojack-blue-600/15 border border-lojack-blue-500/40 flex items-center justify-center">
            <Shield size={18} className="text-lojack-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Audit GDPR scaricati</h3>
            <p className="text-gray-500 text-xs">
              Logbook degli ultimi PDF audit traduzioni — evidenza per ispezione Garante
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/email-preview"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-lojack-blue-600/15 border border-lojack-blue-500/40 text-lojack-blue-300 hover:bg-lojack-blue-600/25 transition"
            data-testid="generate-audit-pdf-link"
          >
            <Download size={12} /> Nuovo audit
          </Link>
          <button
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition disabled:opacity-50"
            data-testid="gdpr-audit-refresh-btn"
            aria-label="Refresh"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="py-8 text-center text-gray-500 text-sm">Caricamento…</div>
      )}

      {data && data.count === 0 && (
        <div className="py-10 text-center" data-testid="gdpr-audit-empty">
          <FileText size={28} className="mx-auto text-gray-600 mb-2" />
          <p className="text-gray-400 text-sm font-medium">Nessun audit ancora generato</p>
          <p className="text-gray-500 text-xs mt-1">
            Scarica il primo audit dall'Email Preview Console per iniziare il logbook
          </p>
        </div>
      )}

      {data && data.count > 0 && (
        <div className="space-y-2.5" data-testid="gdpr-audit-list">
          {data.downloads.map((d) => {
            const fps = d.fingerprints || {};
            return (
              <div
                key={d.id}
                className="bg-[#141414] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition"
                data-testid={`gdpr-audit-row-${d.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">
                      {d.admin_email}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5 font-mono">
                      {formatTs(d.downloaded_at)} · {(d.pdf_size_bytes / 1024).toFixed(0)} KB
                      {d.ip && <span className="ml-2">· IP {d.ip}</span>}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`gdpr-audit-fingerprints-${d.id}`}>
                  {Object.entries(fps).map(([mod, fp]) => (
                    <span
                      key={mod}
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono bg-[#0A0A0A] border border-gray-700/60 rounded-md px-2 py-0.5 text-gray-400"
                      title={mod}
                    >
                      <span className="text-gray-500">{labelFromModule(mod)}</span>
                      <span className="text-lojack-blue-300">{fp}</span>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
          <p className="text-[11px] text-gray-500 italic mt-3 leading-relaxed">
            Ogni download persiste lo snapshot completo dei fingerprint dei catalog hi
            customer-facing. Confronta questi valori con quelli live su{" "}
            <Link to="/admin/i18n-status" className="text-lojack-blue-300 hover:underline">
              Stato i18n
            </Link>{" "}
            per rilevare manomissioni dei testi dopo la firma DPO.
          </p>
        </div>
      )}
    </div>
  );
};
